/**
 * This is a temporary file to hold types that will be changing a lot
 * during the switch refactor. This may remain in some form after,
 * or types may be moved elsewhere.
 */
import { DateTime } from '@mode-switch/express';
import { UserResource } from '../account/user.types';
import { ProducerType, ThoughtspotPublication, TsPublishMetaData } from '../dataset';

// TODO: move this to a more appropriate place once types for GF are fleshed out a bit more
export const enum QueryRunStatus {
  NotYetRun = 'not_yet_run', // this status exists only on the frontend before a query is run for the first time
  Pending = 'pending', // before being put on the queue for the query to be run
  Enqueued = 'enqueued', // after being put on the queue for the query to be run
  Cancelled = 'cancelled',
  Failed = 'failed',
  Succeeded = 'succeeded',
  Completed = 'completed',
  Excluded = 'excluded',
}

export const enum ReportRunStatus {
  Pending = 'pending',
  Enqueued = 'enqueued',
  Cancelled = 'cancelled',
  Failed = 'failed',
  Succeeded = 'succeeded',
  Completed = 'completed',
  RunningNotebook = 'running_notebook',
}

// Possible run types for all Dataset Creators. This type will expend as we build out more
// Dataset Creators, however, this abstraction is useful to start useing now.
export const enum DatasetCreatorRunType {
  QueryRun = 'QueryRun',
}

/**
 * SwitchDataset represents the resolution of the switch view.
 *
 * Still trying to figure out what should be in here v RunResults
 */
export interface SwitchDataset {
  creator: UserResource;
  query_created_at: string;
  query_token: string;
  query_run_token: string;
  query_run_state: QueryRunStatus;
  completed_at: string;
  raw_query: string;
  rendered_query: string;
  query_name: string;
  report_data_url: string;
  report_query_url: string;
  csv_dataset_url: string;
  json_dataset_url: string;
}

/**
 * Exploration data
 * Note: this represents the exploration data in the exploration api endpoint response,
 * at this point, this is not the same as the ExplorationResource.
 */
export interface ExplorationData {
  chart_token: string;
  created_at: string;
  name: string;
  query_name: string;
  query_token: string;
  report_display_name: string;
  report_drilldown_enabled: boolean;
  report_name: string;
  title: string;
  token: string;
  updated_at: string;
}

export const enum SwitchViewSourceType {
  Query = 'Query',
  QueryRun = 'QueryRun',
  SwitchView = 'SwitchView',
}

export interface DatasetResult {
  bytes: number | null;
  completedAt: string;
  contentLength: number | null;
  createdAt: string;
  csvUrl: string;
  datasourceId: number | null;
  dbtMetricToken?: string;
  executedBy: string;
  executedByEmail: string;
  isFallbackData: boolean;
  jsonUrl: string;
  legacyContent?: any[];
  metaColumns: DatasetResourceColumn[];
  oversized: boolean;
  queryCreatedAt: string;
  queryName: string;
  queryRunState: QueryRunStatus;
  queryRunToken: string;
  queryToken: string;
  isMetric?: boolean;
  isPurged: boolean;
  rawQuery: string;
  renderedQuery: string;
  reportDataUrl: string;
  reportQueryUrl: string;
  rowCount: number | null;
  switchViewToken: string;
  token: string;
  datasetToken: string;
  producerType: ProducerType;
  producerToken?: string;
  producerResult?: GoogleSheetResult;
  thoughtspot?: ThoughtspotPublication;
  tsPublishMetaData?: TsPublishMetaData;
  weekStart?: DateTime.WeekStart;
  yearStart?: DateTime.YearStart;
  error: string | null;
}

export interface GoogleSheetResult {
  spreadsheetName: string;
  spreadsheetUrl: string;
  worksheetName: string;
  worksheetRange: string;
  ownedBy: string;
  connectedBy: string;
}

export interface GoogleSheetMetadata extends GoogleSheetResult {
  lastSynced: string;
  lastSyncedBy: string;
}

export enum DatasetStates {
  Orphan = 'orphan',
  Default = 'default',
  HasUpdateAvailable = 'has-update-available',
  LastRunFailed = 'last-run-failed',
}

export interface EnhancedDatasetResult extends DatasetResult {
  dataRefreshAvailableAsOf?: string;
  displayName: string; // This represents the normalized value used to describe the source (ie - Query name v DatasetReport name)
  isLocal: boolean;
  rootSourceModelName: string;
  rootModelIsSample: boolean;
  state: DatasetStates;
  producerType: ProducerType;
  rowOrder: number | null;
}

export interface RunResultResource {
  bytes: number;
  columns: DatasetResourceColumn[];
  content_length: number;
  count: number;
  created_at: string;
  last_modified: string;
  oversized: boolean;
  row_count: number | null;
  state: string; // TODO: Get possible types from BE
  token: string;

  // @TODO (@anthonysimone) [GF] - `?` as temporary fix to properly handle non-dependable values, return to with more robust solution
  _links: {
    csv?: { href: string };
    json?: { href: string };
    web?: { href: string };
  };
}

export interface QueryRunResource {
  completed_at: string;
  created_at: string;
  data_source_id: number;
  dbt_metric_id?: number;
  dbt_metric_token?: string;
  id: number;
  limit: boolean;
  max_result_bytes: number;
  parameters: {};
  purge_started_at: string;
  query_created_at: string;
  query_name: string;
  query_token: string;
  raw_source: string;
  rendered_source: string;
  state: QueryRunStatus;
  token: string;

  _embedded: {
    creator: UserResource;
    result?: RunResultResource;
    producer_run?: SqlProducerRunResource;
  };

  _links: {
    query_web: { href: string };
    result_web: { href: string };
  };
}

export interface QueryResource extends QueryRunResource {
  dataset_thoughtspot_publication_token?: string;
  thoughtspot_published_at?: string;
  thoughtspot_publish_url?: string;

  _embedded: {
    creator: UserResource;
    result?: RunResultResource;
    producer_run?: SqlProducerRunResource;
    thoughtspot_publisher?: UserResource;
    ts_publish_metadata?: TsPublishMetaDataResource;
  };
}

export interface SqlProducerRunResource {
  raw_source: string;
  rendered_source: string;
  data_source_id: number;
}

export interface GoogleSheetProducerRunResource {
  spreadsheet_name: string;
  spreadsheet_url: string;
  worksheet_name: string;
  worksheet_range: string | null;
  owned_by: string;
  connected_by: string;
}

// TODO: Temporarily here - will move it to the DataView section in that PR
export interface DatasetResourceColumn {
  readonly name: string;
  readonly type: string;
}

export interface DatasetMetadata {
  datasetToken?: string;
  isSampleDataset?: boolean;
  lastRefreshed?: string;
  state: EnhancedDatasetResult['state'];
  title?: string;
  producerType?: string;
}

export interface PublishDatasetResource {
  dataset_thoughtspot_publication_token?: string;
  thoughtspot_published_at: string;
  thoughtspot_publish_url?: string;
  _embedded: {
    thoughtspot_publisher?: UserResource;
  };
}

export interface TsPublishMetaDataResource {
  schema_name: string;
  table_name: string;
}

export type PublishResult = ThoughtspotPublication;
